// El gate del paso 09, compartido por las dos pistas de venta.
import type { AppOnboardingState, AppOnboardingStep, AppOnboardingStepState } from '../../types';

/** La única condición que el vendedor puede soltar: la tienda sin plásticos no depende del cliente. */
export const SKIPPABLE_ONBOARDING_STEP: AppOnboardingStep = 'PHYSICAL_CARD';

/** Las que el cliente cumple en el mostrador. Ninguna se da por buena si no vino en la lista. */
const REQUIRED_ONBOARDING_STEPS: AppOnboardingStep[] = [
    'PASSWORD',
    'CONTRACT_SIGNED',
    'SECRET_NUMBER',
];

/**
 * ¿El registro del cliente en su app ya no traba el paso? Sin respuesta del silo —o con cero
 * condiciones leídas— no abre, y con el escape puesto solo deja de contar la tarjeta.
 */
export function onboardingSettled(
    onboarding: AppOnboardingState | undefined,
    cardSkipped: boolean,
): boolean {
    if (!onboarding) return false;
    // Cero condiciones no es «nada pendiente»: es que no leímos ninguna.
    if (onboarding.steps.length === 0) return false;
    // Antes de cualquier veredicto, para que el atajo de `allSettled` pase por la misma defensa.
    if (!clientStepsSettled(onboarding.steps)) return false;
    if (onboarding.allSettled) return true;
    if (!cardSkipped) return false;
    return settledExceptCard(onboarding.steps);
}

/**
 * Las tres del cliente PRESENTES y no pendientes. Se exige quién está y no quién sobra: sobre una
 * lista vacía o mutilada, buscar quién falta la da por buena sin haber verificado nada.
 *
 * Exportada además como el gate de la tarjeta (`CardDeliveryTask`): a diferencia de
 * `onboardingSettled`, esta NO exige `PHYSICAL_CARD` — esa se activa DESPUÉS de tener el plástico
 * en la mano, así que exigirla para poder entregarlo trabaría el paso para siempre.
 */
export function clientStepsSettled(steps: AppOnboardingStepState[]): boolean {
    const statusByStep = new Map(steps.map((s) => [s.step, s.status]));
    return REQUIRED_ONBOARDING_STEPS.every(
        (step) => statusByStep.has(step) && statusByStep.get(step) !== 'PENDING',
    );
}

/** Con el escape puesto solo se suelta la tarjeta: cualquier otra condición sigue trabando. */
function settledExceptCard(steps: AppOnboardingStepState[]): boolean {
    return !steps.some((s) => s.step !== SKIPPABLE_ONBOARDING_STEP && s.status === 'PENDING');
}

/** El cliente ya entró a su app: la instalación se infiere de que empezó a cumplir condiciones. */
export function appReached(steps: AppOnboardingStepState[]): boolean {
    return steps.some((s) => s.status === 'COMPLETED');
}
